import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@1trading/react';
import { ArrowLeftRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { type FieldValueRow, FieldValueTable } from '@/components/field-value-table';
import { CopyButton } from '@/components/ui/copy-button';
import { JsonBlock } from '@/components/ui/json-block';
import { StatusChip } from '@/components/ui/status-chip';
import type { FxRecord } from '@/services/api/fx-swaps';
import { formatDate } from '@/utils/format';
import { formatSnakeCase } from '@/utils/string';
import { FX_STATUS_CHIP_VARIANTS, formatFxNumber } from './fx-swap-format';

// Deliberate fix: legacy iterated Object.keys of the Dynamo item, so the
// field order was nondeterministic — rendered in a fixed declaration order instead,
// skipping keys absent from the record (parity: absent optional attrs never rendered).
const DETAIL_FIELD_ORDER = [
  'p',
  's',
  'status',
  'created_at',
  'client_reference',
  'fast_id',
  'message_id',
  'provider',
  'from_currency',
  'to_currency',
  'amount',
  'customer_price',
  'trade_price',
  'pnl',
  'quote',
  'trade',
  'error_code',
  'error_message',
] as const satisfies readonly (keyof FxRecord)[];

type DetailFieldKey = (typeof DETAIL_FIELD_ORDER)[number];

// trade_price has no entry on purpose: it falls to the snake_case → Title Case
// fallback ('Trade Price'), exactly as the legacy humanizer produced.
const FIELD_LABELS: Partial<Record<DetailFieldKey, string>> = {
  p: 'Partition Key',
  s: 'Sort Key',
  created_at: 'Created At',
  client_reference: 'Client Reference',
  fast_id: 'User ID',
  message_id: 'Message ID',
  provider: 'Provider',
  from_currency: 'From Currency',
  to_currency: 'To Currency',
  amount: 'Amount',
  customer_price: 'Customer Price',
  pnl: 'P&L',
  status: 'Status',
  quote: 'Quote Details',
  trade: 'Trade Details',
  error_code: 'Error Code',
  error_message: 'Error Message',
};

const fieldLabel = (key: DetailFieldKey): string => FIELD_LABELS[key] ?? formatSnakeCase(key);

const renderFieldValue = (record: FxRecord, key: DetailFieldKey): ReactNode => {
  switch (key) {
    // Internal Dynamo keys deliberately visible — ops debugging aid
    case 'p':
      return record.p;
    case 's':
      return record.s;
    case 'status':
      // Deliberate fix: the legacy lowercase-keyed color map never matched the
      // uppercase statuses (always the default gray chip) — SUCCESS → green, FAILED → red.
      return <StatusChip variant={FX_STATUS_CHIP_VARIANTS[record.status]}>{record.status.toUpperCase()}</StatusChip>;
    case 'created_at':
      return formatDate(record.created_at);
    case 'fast_id':
      // Deliberate fix: legacy showed only the truncated id with no tooltip and
      // no way to get the full value — full id + copy button instead.
      return (
        <span className="inline-flex items-center gap-1 font-mono text-xs">
          {record.fast_id}
          <CopyButton label={`Copy ${record.fast_id}`} value={record.fast_id} />
        </span>
      );
    case 'amount':
    case 'customer_price':
    case 'pnl':
      return <span className="tabular-nums">{formatFxNumber(record[key])}</span>;
    case 'trade_price':
      // Deliberate improvement: legacy dropped trade_price into the default
      // `value || 'N/A'` branch — formatted like the other numerics here, aligning with the table.
      return <span className="tabular-nums">{formatFxNumber(record.trade_price)}</span>;
    case 'quote':
    case 'trade': {
      const value = record[key];
      // Pretty JSON inside its own scrollable container — the dialog never overflows the page
      return value === null || value === undefined ? 'N/A' : <JsonBlock className="bg-muted/50" value={value} />;
    }
    default: {
      // Deliberate fix: legacy rendered every falsy value ('' / 0 / false) as
      // 'N/A' — nullish check instead.
      const value = record[key];
      return String(value ?? 'N/A');
    }
  }
};

interface FxSwapDetailDialogProps {
  record: FxRecord;
  onClose: () => void;
}

export const FxSwapDetailDialog = ({ record, onClose }: FxSwapDetailDialogProps) => {
  const rows: FieldValueRow[] = DETAIL_FIELD_ORDER.filter((key) => key in record).map((key) => ({
    key,
    label: fieldLabel(key),
    value: renderFieldValue(record, key),
  }));

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="sm:max-w-[1000px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="size-5" />
            FX Swap Details: {record.client_reference || 'N/A'}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[65vh] overflow-y-auto rounded-md border">
          <FieldValueTable rows={rows} />
        </div>
        <DialogFooter>
          <Button onClick={onClose} type="button">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
FxSwapDetailDialog.displayName = 'FxSwapDetailDialog';
